import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { candidateService, donationService } from '../../services';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import PartyDonationModal from '../../components/common/PartyDonationModal';

const Home = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [showPartyDonateModal, setShowPartyDonateModal] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [candidatesData, statsData] = await Promise.all([
        candidateService.getAllCandidates({ limit: 6, offset: 0, sortBy: 'id', order: 'DESC' }),
        donationService.getPublicStats(),
      ]);

      // Handle API response structure: { status, statusCode, data: { items: [], totalCount } }
      const candidatesList = candidatesData?.data?.items || [];
      const stats = {
        totalRaised: statsData?.data?.totalAmountRaised || 0,
        totalDonors: statsData?.data?.totalDonors || 0,
        totalCandidates: candidatesData?.data?.totalCandidates || candidatesList.length,
      };

      setCandidates(candidatesList);
      setStats(stats);
    } catch (error) {
      console.error('Error fetching data:', error);
      setCandidates([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-primary-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-primary-50 max-w-3xl mx-auto">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowPartyDonateModal(true)}
                className="inline-flex items-center justify-center bg-white text-primary-700 px-8 py-4 rounded-button font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('nav.donateToParty')}
              </button>
              <Link
                to="/candidates"
                className="inline-flex items-center justify-center bg-primary-500 bg-opacity-20 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-button font-semibold hover:bg-opacity-30 transition-all duration-200"
              >
                {t('home.viewCandidates')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              {/* Check KYC Status Button - Commented Out
              <Link
                to="/check-kyc-status"
                className="inline-flex items-center justify-center bg-orange-500 bg-opacity-10 backdrop-blur-sm border-2 border-orange-400 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-600 hover:bg-opacity-20 transition-all duration-200"
              >
                {t('home.checkKycStatus')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Link>
              */}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-600 rounded-lg mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-primary-700 mb-2">
                  {formatCurrency(stats.totalRaised || 0, lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalRaised')}</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-600 rounded-lg mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-green-700 mb-2">
                  {formatNumber(stats.totalDonors || 0, lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalDonors')}</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-600 rounded-lg mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-purple-700 mb-2">
                  {formatNumber(stats.totalCandidates || (Array.isArray(candidates) ? candidates.length : 0), lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalCandidates')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured Candidates */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.featuredCandidates')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('home.heroSubtitle')}
          </p>
        </div>
        {Array.isArray(candidates) && candidates.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {candidates.slice(0, 6).map((candidate) => (
                <div key={candidate.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
                  <div className="h-56 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
                    {candidate.photoKeyUrl && !imageErrors[candidate.id] ? (
                      <img
                        src={candidate.photoKeyUrl}
                        alt={candidate.name}
                        className="w-full h-full object-cover"
                        onError={() => {
                          setImageErrors(prev => ({ ...prev, [candidate.id]: true }));
                        }}
                      />
                    ) : (
                      <span className="text-5xl font-bold text-primary-600">
                        {candidate.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{candidate.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {candidate.constituency}, {candidate.district}
                    </p>
                    <div className="mb-4">

                    </div>
                    <Link
                      to={`/candidates/${candidate.id}`}
                      className="block w-full text-center bg-primary-600 text-white py-2 rounded-button hover:bg-primary-700 transition"
                    >
                      {t('actions.viewProfile')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/candidates"
                className="inline-block bg-primary-600 text-white px-8 py-3 rounded-button font-semibold hover:bg-primary-700 transition"
              >
                {t('home.viewAllCandidates')}
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('home.noFeaturedCandidates')}</p>
          </div>
        )}
      </div>

      {/* Party Donation Modal */}
      <PartyDonationModal
        isOpen={showPartyDonateModal}
        onClose={() => setShowPartyDonateModal(false)}
      />
    </div>
  );
};

export default Home;
